import { useState, useEffect, useRef } from 'react';
import { collection, addDoc, query, orderBy, onSnapshot, serverTimestamp, doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase/config';
import { useAuthState } from '../hooks/useAuthState';
import '../styles/MessageBoard.css';

const MessageBoard = ({ eventId, event, boardType }) => {
  const { user } = useAuthState();
  const [messages, setMessages] = useState([]);
  const [messagesWithUsers, setMessagesWithUsers] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [replyingTo, setReplyingTo] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [loading, setLoading] = useState(true);
  const [activeBoardType, setActiveBoardType] = useState(boardType || 'preEvent');
  const [userCache, setUserCache] = useState({});
  const [currentUserData, setCurrentUserData] = useState(null);
  const messagesEndRef = useRef(null);

  // Load current user's profile data
  useEffect(() => {
    if (!user) {
      setCurrentUserData(null);
      return;
    }

    getUserData(user.uid).then(data => {
      setCurrentUserData(data);
    });
  }, [user]);

  // Determine active board type based on event timing
  useEffect(() => {
    if (!event || boardType) {
      // If boardType is explicitly provided, use it
      if (boardType) {
        setActiveBoardType(boardType);
      }
      return;
    }

    const now = new Date();
    const startTime = event.startsAt?.toDate ? event.startsAt.toDate() : new Date(event.startsAt);
    const endTime = event.endsAt?.toDate ? event.endsAt.toDate() : startTime;
    
    // Check if decompression board is enabled and opened
    if (event.messageBoards?.decompression?.enabled && event.messageBoards?.decompression?.opened) {
      const decompressionEnd = new Date(endTime.getTime() + (event.messageBoards.decompression.durationDays || 7) * 24 * 60 * 60 * 1000);
      if (now <= decompressionEnd) {
        setActiveBoardType('decompression');
        return;
      }
    }
    
    // During event
    if (now >= startTime && now <= endTime) {
      if (event.messageBoards?.duringEvent) {
        setActiveBoardType('duringEvent');
        return;
      }
    }
    
    // Pre-event (default)
    if (event.messageBoards?.preEvent) {
      setActiveBoardType('preEvent');
      return;
    }
    
    // Fallback to first available board
    if (event.messageBoards?.preEvent) {
      setActiveBoardType('preEvent');
    } else if (event.messageBoards?.duringEvent) {
      setActiveBoardType('duringEvent');
    } else if (event.messageBoards?.decompression?.enabled) {
      setActiveBoardType('decompression');
    }
  }, [event, boardType]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Load user data for a userId
  const getUserData = async (userId) => {
    if (userCache[userId]) {
      return userCache[userId];
    }

    try {
      const userDoc = await getDoc(doc(db, 'users', userId));
      if (userDoc.exists()) {
        const data = userDoc.data();
        const userData = {
          firstName: data.firstName || '',
          lastName: data.lastName || '',
          profilePicture: data.profilePicture || null,
        };
        setUserCache(prev => ({ ...prev, [userId]: userData }));
        return userData;
      }
    } catch (error) {
      console.error('Error loading user data:', error);
    }

    return { firstName: '', lastName: '', profilePicture: null };
  };

  useEffect(() => {
    if (!eventId || !activeBoardType) return;

    // Try the correct path structure: events/{eventId}/messageBoards/{boardType}/messages
    const messagesRef = collection(db, 'events', eventId, 'messageBoards', activeBoardType, 'messages');
    const q = query(messagesRef, orderBy('createdAt', 'asc'));

    setLoading(true);

    const unsubscribe = onSnapshot(
      q,
      async (snapshot) => {
        const messagesList = [];
        snapshot.forEach((doc) => {
          const data = doc.data();
          messagesList.push({
            id: doc.id,
            ...data,
            createdAt: data.createdAt?.toDate() || new Date(),
            parentId: data.parentId || null, // For replies
          });
        });
        setMessages(messagesList);

        // Load user data for all unique user IDs
        const uniqueUserIds = [...new Set(messagesList.map(m => m.userId))];
        const userDataPromises = uniqueUserIds.map(userId => getUserData(userId));
        const userDataArray = await Promise.all(userDataPromises);
        const userDataMap = {};
        uniqueUserIds.forEach((userId, index) => {
          userDataMap[userId] = userDataArray[index];
        });

        // Attach user data to messages
        const messagesWithUserData = messagesList.map(msg => ({
          ...msg,
          userData: userDataMap[msg.userId] || { firstName: '', lastName: '', profilePicture: null }
        }));
        
        setMessagesWithUsers(messagesWithUserData);
        setLoading(false);
        setTimeout(scrollToBottom, 100);
      },
      (error) => {
        console.error('Error loading messages:', error);
        setLoading(false);
        // If the collection doesn't exist, that's okay - just show empty state
      }
    );

    return () => unsubscribe();
  }, [eventId, activeBoardType]);

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !user || !activeBoardType) return;

    try {
      const messagesRef = collection(db, 'events', eventId, 'messageBoards', activeBoardType, 'messages');
      await addDoc(messagesRef, {
        userId: user.uid,
        text: newMessage.trim(),
        createdAt: serverTimestamp(),
        parentId: null, // Top-level comment
      });
      setNewMessage('');
      scrollToBottom();
    } catch (error) {
      console.error('Error sending message:', error);
      alert('Failed to send message. Please try again.');
    }
  };

  const handleSendReply = async (parentId) => {
    if (!replyText.trim() || !user || !activeBoardType) return;

    try {
      const messagesRef = collection(db, 'events', eventId, 'messageBoards', activeBoardType, 'messages');
      await addDoc(messagesRef, {
        userId: user.uid,
        text: replyText.trim(),
        createdAt: serverTimestamp(),
        parentId: parentId, // Reply to this message
      });
      setReplyText('');
      setReplyingTo(null);
      scrollToBottom();
    } catch (error) {
      console.error('Error sending reply:', error);
      alert('Failed to send reply. Please try again.');
    }
  };

  const formatTime = (date) => {
    const d = new Date(date);
    const now = new Date();
    const diffMs = now - d;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    
    return d.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: d.getFullYear() !== now.getFullYear() ? 'numeric' : undefined,
    });
  };

  // Organize messages into threads (top-level comments with replies)
  const organizeMessages = () => {
    const topLevel = messagesWithUsers.filter(m => !m.parentId);
    const replies = messagesWithUsers.filter(m => m.parentId);
    
    return topLevel.map(comment => ({
      ...comment,
      replies: replies
        .filter(r => r.parentId === comment.id)
        .sort((a, b) => a.createdAt - b.createdAt)
    })).sort((a, b) => a.createdAt - b.createdAt);
  };

  const getInitials = (firstName, lastName) => {
    const first = firstName?.charAt(0) || '';
    const last = lastName?.charAt(0) || '';
    return (first + last).toUpperCase() || '?';
  };

  const getDisplayName = (userData, userId) => {
    if (userId === user?.uid) return 'You';
    if (userData?.firstName && userData?.lastName) {
      return `${userData.firstName} ${userData.lastName}`;
    }
    if (userData?.firstName) return userData.firstName;
    return `User ${userId.slice(0, 8)}...`;
  };

  const getBoardInfo = () => {
    switch (activeBoardType) {
      case 'preEvent':
        return {
          title: 'Pre-Event Message Board',
          subtitle: 'Make plans, get excited!'
        };
      case 'duringEvent':
        return {
          title: 'Event Message Board',
          subtitle: 'Last minute rides, and what\'s going on!'
        };
      case 'decompression':
        return {
          title: 'Post-Event Message Board',
          subtitle: 'Decompress, reminisce, find lost connections'
        };
      default:
        return {
          title: 'Message Board',
          subtitle: ''
        };
    }
  };

  const boardInfo = getBoardInfo();

  if (loading) {
    return (
      <div className="message-board-container">
        <div className="loading">Loading messages...</div>
      </div>
    );
  }

  return (
    <div className="message-board-container">
      <div className="message-board-header">
        <h3>{boardInfo.title}</h3>
        {boardInfo.subtitle && (
          <p className="board-hint">{boardInfo.subtitle}</p>
        )}
      </div>

      <div className="messages-list" ref={messagesEndRef}>
        {messagesWithUsers.length === 0 ? (
          <div className="empty-messages">
            <p>No messages yet. Be the first to post! 🎉</p>
          </div>
        ) : (
          organizeMessages().map((comment) => {
            const userData = comment.userData || {};
            const displayName = getDisplayName(userData, comment.userId);
            const initials = getInitials(userData.firstName, userData.lastName);
            const isOwn = comment.userId === user?.uid;

            return (
              <div key={comment.id} className="comment-item">
                <div className="comment-avatar">
                  {userData.profilePicture ? (
                    <img 
                      src={userData.profilePicture} 
                      alt={displayName}
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                  ) : (
                    <div className="comment-avatar-fallback">
                      {initials}
                    </div>
                  )}
                </div>
                <div className="comment-content">
                  <div className="comment-header">
                    <span className="comment-author">{displayName}</span>
                    <span className="comment-time">{formatTime(comment.createdAt)}</span>
                  </div>
                  <div className="comment-text">{comment.text}</div>
                  {user && (
                    <button 
                      className="comment-reply-btn"
                      onClick={() => setReplyingTo(replyingTo === comment.id ? null : comment.id)}
                    >
                      Reply
                    </button>
                  )}
                  
                  {/* Reply input */}
                  {replyingTo === comment.id && (
                    <div className="comment-reply-form">
                      <input
                        type="text"
                        className="comment-reply-input"
                        placeholder="Write a reply..."
                        value={replyText}
                        onChange={(e) => setReplyText(e.target.value)}
                        autoFocus
                      />
                      <div className="comment-reply-actions">
                        <button
                          className="comment-reply-send"
                          onClick={() => handleSendReply(comment.id)}
                          disabled={!replyText.trim()}
                        >
                          Reply
                        </button>
                        <button
                          className="comment-reply-cancel"
                          onClick={() => {
                            setReplyingTo(null);
                            setReplyText('');
                          }}
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Replies */}
                  {comment.replies && comment.replies.length > 0 && (
                    <div className="comment-replies">
                      {comment.replies.map((reply) => {
                        const replyUserData = reply.userData || {};
                        const replyDisplayName = getDisplayName(replyUserData, reply.userId);
                        const replyInitials = getInitials(replyUserData.firstName, replyUserData.lastName);
                        
                        return (
                          <div key={reply.id} className="comment-reply-item">
                            <div className="comment-reply-avatar">
                              {replyUserData.profilePicture ? (
                                <img 
                                  src={replyUserData.profilePicture} 
                                  alt={replyDisplayName}
                                  onError={(e) => { e.target.style.display = 'none'; }}
                                />
                              ) : (
                                <div className="comment-reply-avatar-fallback">
                                  {replyInitials}
                                </div>
                              )}
                            </div>
                            <div className="comment-reply-content">
                              <div className="comment-reply-header">
                                <span className="comment-reply-author">{replyDisplayName}</span>
                                <span className="comment-reply-time">{formatTime(reply.createdAt)}</span>
                              </div>
                              <div className="comment-reply-text">{reply.text}</div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            );
          })
        )}
        <div ref={messagesEndRef} />
      </div>

      {user && (
        <form onSubmit={handleSendMessage} className="message-input-form">
          <div className="message-input-wrapper">
            {currentUserData?.profilePicture ? (
              <img 
                src={currentUserData.profilePicture} 
                alt="You"
                className="message-input-avatar"
                onError={(e) => { e.target.style.display = 'none'; }}
              />
            ) : (
              <div className="message-input-avatar message-input-avatar-fallback">
                {getInitials(currentUserData?.firstName || '', currentUserData?.lastName || '')}
              </div>
            )}
            <input
              type="text"
              className="message-input"
              placeholder="Write a comment..."
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
            />
          </div>
          {newMessage.trim() && (
            <button type="submit" className="btn btn-primary message-send-btn">
              Post
            </button>
          )}
        </form>
      )}
    </div>
  );
};

export default MessageBoard;

